import Checkbox from '@mui/material/Checkbox/index.js'
import FormControlLabel from '@mui/material/FormControlLabel/index.js'
import Tooltip from '@mui/material/Tooltip/index.js'
import Typography from '@mui/material/Typography/index.js'
import classNames from 'classnames'

import { huggingMachine } from '../../../data/items.js'
import { genders } from '../../../enums.js'
import { COW_COLOR_NAMES } from '../../../strings.js'
import { colors } from '../../../styles/tokens.js'
import { getCowWeight } from '../../../utils/getCowWeight.js'
import { getPlayerName } from '../../../utils/getPlayerName.js'
import { integerString } from '../../../utils/integerString.js'
import { isCowInBreedingPen } from '../../../utils/isCowInBreedingPen.js'
import { memoize } from '../../../utils/memoize.js'
import { moneyString } from '../../../utils/moneyString.js'
import { nullArray } from '../../../utils/nullArray.js'
import { HeartIcon, HeartOutlineIcon } from '../../PixelIcon/index.js'
import Bloodline from '../Bloodline/index.js'
import { CowCardProps } from '../CowCard.js'

// The extra 0.5 is for rounding up to the next full heart. This allows a fully
// happy cow to have full hearts on the beginning of a new day.
const isHeartFull = (heartIndex: number, numberOfFullHearts: number) =>
  heartIndex + 0.5 < numberOfFullHearts

const getCowMapById = memoize((cowInventory: farmhand.state['cowInventory']) =>
  cowInventory.reduce(
    (acc: Record<string, farmhand.cow>, cow: farmhand.cow) => {
      acc[cow.id] = cow
      return acc
    },
    {}
  )
)

export interface SubheaderProps extends Pick<
  CowCardProps,
  | 'cow'
  | 'cowBreedingPen'
  | 'cowIdOfferedForTrade'
  | 'cowInventory'
  | 'handleCowAutomaticHugChange'
  | 'handleCowBreedChange'
  | 'huggingMachinesRemain'
  | 'playerId'
> {
  canCowBeTradedFor: boolean
  cowValue: number
  isCowPurchased: boolean
}

const Subheader = ({
  canCowBeTradedFor,
  cow,
  cowBreedingPen,
  cowIdOfferedForTrade,
  cowInventory,
  cowValue,
  handleCowAutomaticHugChange,
  handleCowBreedChange,
  huggingMachinesRemain,
  playerId,
  isCowPurchased,
}: SubheaderProps) => {
  const numberOfFullHearts = cow.happiness * 10
  const isInBreedingPen = isCowInBreedingPen(cow, cowBreedingPen)
  const isRoomInBreedingPen =
    cowBreedingPen.cowId1 === null || cowBreedingPen.cowId2 === null
  const isThisCowOfferedForTrade = cowIdOfferedForTrade === cow.id

  const mateId = cowBreedingPen.cowId1 ?? cowBreedingPen.cowId2
  const mate = getCowMapById(cowInventory)[mateId ?? '']
  const isEligibleToBreed = cow.gender !== mate?.gender

  const canBeMovedToBreedingPen =
    isRoomInBreedingPen && isEligibleToBreed && !isThisCowOfferedForTrade

  const disableBreedingControlTooltip =
    !canBeMovedToBreedingPen && !isInBreedingPen

  const showOriginalOwner =
    isCowPurchased &&
    playerId !== cow.originalOwnerId &&
    playerId === cow.ownerId

  return (
    <div {...{ className: 'Subheader' }}>
      <Bloodline {...{ colorsInBloodline: cow.colorsInBloodline }} />
      {(isCowPurchased || cowIdOfferedForTrade) && (
        <p>
          {cow.daysOld} {cow.daysOld === 1 ? 'day' : 'days'} old
        </p>
      )}
      <p>Color: {COW_COLOR_NAMES[cow.color as keyof typeof COW_COLOR_NAMES]}</p>
      <p>
        {/* cow.originalOwnerId is only an empty string when it is for sale. */}
        {cow.originalOwnerId === '' ? 'Price' : 'Value'}:{' '}
        {moneyString(cowValue)}
      </p>
      <p>Weight: {getCowWeight(cow)} lbs.</p>
      {(isCowPurchased || canCowBeTradedFor) && (
        <p>Times traded: {integerString(cow.timesTraded)}</p>
      )}
      {showOriginalOwner && (
        <p>
          Original owner: <strong>{getPlayerName(cow.originalOwnerId)}</strong>
        </p>
      )}
      {isCowPurchased && (
        <>
          <ol className="hearts">
            {nullArray(10).map((_null: null, i: number) => (
              <li key={`${cow.id}_${i}`}>
                {isHeartFull(i, numberOfFullHearts) ? (
                  <HeartIcon
                    className="heart is-full"
                    fontSize="inherit"
                    htmlColor={colors.heart}
                  />
                ) : (
                  <HeartOutlineIcon className="heart" fontSize="inherit" />
                )}
              </li>
            ))}
          </ol>
          <Tooltip
            {...{
              placement: 'top',
              title: (
                <Typography>
                  Check this box to put {cow.name} into a {huggingMachine.name}{' '}
                  and automatically hug them at the start of every day. Requires
                  a Hugging Machine in your inventory.
                </Typography>
              ),
            }}
          >
            <FormControlLabel
              {...{
                control: (
                  <Checkbox
                    {...{
                      color: 'primary',
                      checked: cow.isUsingHuggingMachine,
                      onChange: e =>
                        handleCowAutomaticHugChange &&
                        handleCowAutomaticHugChange(e, cow),
                    }}
                  />
                ),
                disabled: !cow.isUsingHuggingMachine && !huggingMachinesRemain,
                label: 'Hug automatically',
              }}
            />
          </Tooltip>
          <Tooltip
            {...{
              placement: 'top',
              disableFocusListener: disableBreedingControlTooltip,
              disableHoverListener: disableBreedingControlTooltip,
              disableTouchListener: disableBreedingControlTooltip,
              title: (
                <Typography>
                  {isInBreedingPen
                    ? `Uncheck this box to return ${cow.name} to the regular pen.`
                    : `Check this box to move ${
                        cow.name
                      } to the breeding pen to mate with a ${
                        cow.gender === genders.MALE ? 'female' : 'male'
                      } cow.`}
                </Typography>
              ),
            }}
          >
            <FormControlLabel
              {...{
                control: (
                  <Checkbox
                    {...{
                      color: 'primary',
                      checked: isInBreedingPen,
                      inputProps: {
                        'aria-label': 'move cow to breeding pen',
                      } as any,
                      onChange: e =>
                        handleCowBreedChange && handleCowBreedChange(e, cow),
                    }}
                  />
                ),
                disabled: isInBreedingPen ? false : !canBeMovedToBreedingPen,
                label: 'Breed',
              }}
            />
          </Tooltip>
        </>
      )}
    </div>
  )
}

export default Subheader
